/** Atleta montado na vassoura, pronto para a cena do estádio (usado pelo visualizador e pela prévia de voo). */
import * as THREE from "three";
import {
  CLIPS,
  SEATED,
  addPoses,
  flightLayer,
  lerpPoses,
  sampleClip,
  surgeLayer,
  type Clip,
} from "@besenliga/anim";
import {
  applyPose,
  buildAthlete,
  buildBroom,
  captureRest,
  gripBroom,
  lodForDistance,
  mountOnBroom,
  setLod,
  type AthleteModels,
  type AthleteRig,
  type AthleteSpec,
  type Broom,
  type RestPoses,
} from "@besenliga/athlete-three";
import { ATHLETE_VIEW_SCALE, type GraphicsSettings, type Kit } from "@besenliga/shared";

export interface Rider {
  rig: AthleteRig;
  rest: RestPoses;
  broom: Broom;
  /** Raiz que a cena move e gira: o centro é a vassoura, sob a pelve. */
  holder: THREE.Group;
  lod: number;
  /** Estado suavizado da animação (ver `animateRider`). */
  anim: RiderAnim;
}

/**
 * Suavização da animação: giro e aceleração chegam da simulação a cada tick (30 Hz) e mudariam a pose em
 * degraus; aqui eles seguem o valor novo aos poucos. O turbo entra e sai num esmaecimento curto, e um clipe de
 * ação (passe, arremesso, rebatida…) toca por cima da pose de voo e volta para ela sem corte.
 */
export interface RiderAnim {
  turn: number;
  accel: number;
  turboWeight: number;
  action: { clip: Clip; start: number } | null;
}

/** Constantes de tempo da suavização (1/s): maior = segue mais rápido. */
const SMOOTH = { turn: 8, accel: 6, turbo: 6 };
/** Entrada e saída do clipe de ação, em segundos. */
const ACTION_BLEND = 0.12;

export function createRider(
  models: AthleteModels,
  spec: AthleteSpec,
  kit: Kit,
  settings: GraphicsSettings,
): Rider {
  const rig = buildAthlete(models, spec, kit, { outline: settings.outlinePx });
  const rest = captureRest(rig.root);
  applyPose(rig.root, rest, SEATED);
  const broom = buildBroom(kit);
  const holder = mountOnBroom(rig, broom);
  holder.scale.setScalar(ATHLETE_VIEW_SCALE);
  if (settings.shadowMapSize === 0)
    holder.traverse((o) => {
      o.castShadow = false;
    });
  return { rig, rest, broom, holder, lod: 0, anim: { turn: 0, accel: 0, turboWeight: 0, action: null } };
}

/** Estado de voo para a animação (vem da simulação, já interpolado). */
export interface RiderMotion {
  /** Giro da vassoura em rad/s (positivo = curva para a esquerda). */
  turnRate: number;
  climb: number;
  speed: number;
  turbo: boolean;
  /** Aceleração na direção da vassoura (m/s²). */
  accel?: number;
  /** Giro extra em torno do eixo da vassoura (rad), para a Finta Espiral. */
  spin?: number;
}

/** Toca um clipe de ação (passe, arremesso, rebatida, defesa, agarrar, atordoado) a partir de `time`. */
export function playAction(r: Rider, clip: Clip, time: number): void {
  r.anim.action = { clip, start: time };
}

/**
 * Aplica a pose de voo: sentado (ou turbo), inclinação da curva, arranque ou freada, e o clipe de ação por cima.
 * `yaw` é a direção da vassoura e `dt` o tempo do quadro, para suavizar.
 */
export function animateRider(r: Rider, time: number, yaw: number, motion: RiderMotion, dt = 1 / 60): void {
  const a = r.anim;
  const follow = (rate: number) => 1 - Math.exp(-rate * Math.max(0, dt));
  a.turn += (motion.turnRate - a.turn) * follow(SMOOTH.turn);
  a.accel += ((motion.accel ?? 0) - a.accel) * follow(SMOOTH.accel);
  a.turboWeight += ((motion.turbo ? 1 : 0) - a.turboWeight) * follow(SMOOTH.turbo);
  const fl = flightLayer({ turnRate: a.turn, climb: motion.climb, speed: motion.speed, accel: a.accel });
  r.holder.rotation.set(-fl.pitch, yaw, -fl.roll - (motion.spin ?? 0), "YXZ");
  r.holder.updateMatrixWorld(true);

  const cruise = sampleClip(CLIPS.sentado, time);
  const boost = sampleClip(CLIPS.turbo, CLIPS.turbo.duration);
  let pose = lerpPoses(cruise.pose, boost.pose, a.turboWeight);
  pose = addPoses(pose, CLIPS.curva.keys[0]?.layer ?? {}, fl.lean);
  pose = addPoses(pose, surgeLayer(fl.surge));
  let grip = cruise.grip;

  if (a.action) {
    const t = time - a.action.start;
    const dur = a.action.clip.duration;
    if (t >= dur + ACTION_BLEND) a.action = null;
    else {
      const w = Math.min(1, t / ACTION_BLEND, (dur + ACTION_BLEND - t) / ACTION_BLEND);
      const s = sampleClip(a.action.clip, Math.min(t, dur));
      pose = lerpPoses(pose, addPoses(s.pose, surgeLayer(fl.surge)), w);
      grip = { l: grip.l + (s.grip.l - grip.l) * w, r: grip.r + (s.grip.r - grip.r) * w };
    }
  }
  applyPose(r.rig.root, r.rest, pose);
  gripBroom(r.rig, r.broom, grip);
}

/** Troca o nível de detalhe pela distância até a câmera (corrigida pela escala de exibição e pelo preset). */
export function updateRiderLod(r: Rider, camera: THREE.Camera, settings: GraphicsSettings): void {
  const d = camera.position.distanceTo(r.holder.position) / (ATHLETE_VIEW_SCALE * settings.lodDistanceScale);
  const lod = lodForDistance(d);
  if (lod !== r.lod) {
    setLod(r.rig, lod);
    r.lod = lod;
  }
}

/** Sombras-bolha (preset sem sombra em tempo real): um disco por atleta, numa malha instanciada. */
export class BlobShadows {
  readonly mesh: THREE.InstancedMesh;
  private readonly m = new THREE.Matrix4();

  constructor(count: number) {
    const disc = new THREE.CircleGeometry(1.1, 20);
    disc.rotateX(-Math.PI / 2);
    this.mesh = new THREE.InstancedMesh(
      disc,
      new THREE.MeshBasicMaterial({ color: 0x1d2a14, transparent: true, opacity: 0.35, depthWrite: false }),
      count,
    );
    this.mesh.name = "Sombras_bolha";
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 1;
  }

  /** O disco encolhe conforme o atleta sobe: o tamanho indica a altura. */
  update(riders: readonly Rider[]): void {
    riders.forEach((r, i) => {
      const p = r.holder.position;
      const k = THREE.MathUtils.clamp(1.3 - p.y / 40, 0.5, 1.2) * ATHLETE_VIEW_SCALE * 0.5;
      this.m.makeScale(k, 1, k).setPosition(p.x, 0.04, p.z);
      this.mesh.setMatrixAt(i, this.m);
    });
    this.mesh.instanceMatrix.needsUpdate = true;
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    (this.mesh.material as THREE.Material).dispose();
    this.mesh.removeFromParent();
  }
}
